/**
 * WebSocket Hook
 * Integration between Socket.io events and TanStack Query cache
 * Uses infinite query format for message pagination
 */

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { InfiniteData } from '@tanstack/react-query';
import type { PaginatedMessagesResponse } from '@/services/messages.service';
import { toast } from 'sonner';
import socketClient from '@/services/socket.client';
import type {
  Message,
  Channel,
  TypingIndicator,
  MessageEditedEvent,
  MessageDeletedEvent,
  MessageUpdatedEvent,
  NewMessageEvent,
  ReactionEvent,
  MessageReaction,
  MentionEvent,
  PresenceUpdate,
} from '@/types/api.types';
import { useAppStore } from '@/store/app.store';
import { useAuthStore } from '@/store/auth.store';
import { usePresenceStore } from '@/store/presence.store';
import type { UserStatus } from '@/utils/presence.utils';
import { soundService, type SoundType } from '@/services/sound.service';
import { useUserSettings } from '@/hooks/useUserSettings';

interface ChannelUpdatedEvent {
  channel: Channel;
}

interface ChannelDeletedEvent {
  channelId: string;
}

/**
 * Helper type for infinite query data
 */
type InfiniteMessagesData = InfiniteData<PaginatedMessagesResponse>;

/**
 * Hook to manage WebSocket connection and cache updates
 */
export const useWebSocket = () => {
  const queryClient = useQueryClient();
  const { activeChannelId, activeDMThreadId, addTypingUser, removeTypingUser, addMention, updateMentionCount } = useAppStore();
  const { user: currentUser } = useAuthStore();
  const { data: userSettings } = useUserSettings();
  const isConnectedRef = useRef(false);

  // Refs to access current values inside event handlers
  const currentUserRef = useRef(currentUser);
  const activeChannelRef = useRef(activeChannelId);
  const activeDMRef = useRef(activeDMThreadId);
  const settingsRef = useRef(userSettings);

  // Keep refs updated
  useEffect(() => {
    currentUserRef.current = currentUser;
    activeChannelRef.current = activeChannelId;
    settingsRef.current = userSettings;
    activeDMRef.current = activeDMThreadId;
  });

  useEffect(() => {
    // Handle new message event
    const handleNewMessage = (data: NewMessageEvent) => {
      console.log('🔔 WebSocket: Received message:new event', {
        messageId: data.message?.id,
        channelId: data.channelId,
        dmThreadId: data.dmThreadId,
        hasMessage: !!data.message
      });

      const conversationKey = data.channelId || data.dmThreadId;
      const queryKey = ['messages', 'infinite', conversationKey];

      queryClient.setQueryData<InfiniteMessagesData>(queryKey, (old) => {
        if (!old?.pages?.length) {
          // Initialize with new message if no data exists
          return {
            pages: [{
              messages: [data.message],
              hasOlderMessages: false,
              hasNewerMessages: false,
              oldestMessageId: data.message.id,
              newestMessageId: data.message.id,
            }],
            pageParams: [undefined],
          };
        }

        // Check if message already exists in any page
        for (const page of old.pages) {
          if (page.messages.some((msg) => msg.id === data.message.id)) {
            console.log('🔄 Message already exists, skipping:', data.message.id);
            return old;
          }
        }

        // Add new message to the first page (most recent messages)
        const newPages = [...old.pages];
        newPages[0] = {
          ...newPages[0],
          messages: [...newPages[0].messages, data.message],
          newestMessageId: data.message.id,
        };

        console.log('✅ Added new message:', data.message.id);
        return { ...old, pages: newPages };
      });

      // Play notification sound for messages from other users
      const isOwnMessage = currentUserRef.current && data.message.user_id === currentUserRef.current.id;
      const isActiveConversation =
        (data.channelId && data.channelId === activeChannelRef.current) ||
        (data.dmThreadId && data.dmThreadId === activeDMRef.current);

      if (!isOwnMessage) {
        // Play sound from user settings, with reduced volume if viewing the conversation
        const s = settingsRef.current;
        const isDM = !!data.dmThreadId;
        const soundType = (isDM ? s?.dm_sound : s?.message_sound) || 'default';
        const baseVolume = (isDM ? s?.dm_volume : s?.message_volume) ?? 50;
        const volume = isActiveConversation ? Math.round(baseVolume * 0.6) : baseVolume;
        soundService.playNotification(soundType as SoundType, volume);

        // Update unread count for channels not currently active
        if (data.channelId && !isActiveConversation) {
          queryClient.invalidateQueries({ queryKey: ['channels'] });
        }

        // Update DM thread list (unread count + last_message) for non-active DMs
        if (data.dmThreadId && !isActiveConversation) {
          queryClient.invalidateQueries({ queryKey: ['dm-threads'] });
        }
      }

      // For own messages in DMs, update thread list so last_message/ordering refreshes
      if (isOwnMessage && data.dmThreadId) {
        queryClient.invalidateQueries({ queryKey: ['dm-threads'] });
      }
    };

    // Handle message edited event
    const handleMessageEdited = (data: MessageEditedEvent) => {
      const conversationKey = data.channelId || data.dmThreadId;
      const queryKey = ['messages', 'infinite', conversationKey];

      queryClient.setQueryData<InfiniteMessagesData>(queryKey, (old) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            messages: page.messages.map((msg) =>
              msg.id === data.messageId
                ? { ...msg, content: data.content, is_edited: true, edited_at: data.editedAt }
                : msg
            ),
          })),
        };
      });

      // Update single message cache if it exists
      queryClient.setQueryData(['messages', 'single', data.messageId], (old: Message | undefined) => {
        if (!old) return old;
        return { ...old, content: data.content, is_edited: true, edited_at: data.editedAt };
      });
    };

    // Handle message deleted event
    const handleMessageDeleted = (data: MessageDeletedEvent) => {
      const conversationKey = data.channelId || data.dmThreadId;
      const queryKey = ['messages', 'infinite', conversationKey];

      queryClient.setQueryData<InfiniteMessagesData>(queryKey, (old) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            messages: page.messages.filter((msg) => msg.id !== data.messageId),
          })),
        };
      });

      // Remove single message cache
      queryClient.removeQueries({ queryKey: ['messages', 'single', data.messageId] });
    };

    // Handle message updated event (for attachments and other updates)
    const handleMessageUpdated = (data: MessageUpdatedEvent) => {
      const conversationKey = data.channelId || data.dmThreadId;
      const queryKey = ['messages', 'infinite', conversationKey];

      queryClient.setQueryData<InfiniteMessagesData>(queryKey, (old) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            messages: page.messages.map((msg) =>
              msg.id === data.message.id ? data.message : msg
            ),
          })),
        };
      });

      // Update single message cache if it exists
      queryClient.setQueryData(['messages', 'single', data.message.id], data.message);
    };

    // Handle channel updated event
    const handleChannelUpdated = (data: ChannelUpdatedEvent) => {
      // Update channel list
      queryClient.setQueryData<Channel[]>(['channels'], (old) => {
        if (!old) return [data.channel];
        return old.map((channel) =>
          channel.id === data.channel.id ? data.channel : channel
        );
      });

      // Update single channel cache
      queryClient.setQueryData(['channels', data.channel.id], data.channel);
    };

    // Handle channel deleted event
    const handleChannelDeleted = (data: ChannelDeletedEvent) => {
      // Remove from channel list
      queryClient.setQueryData<Channel[]>(['channels'], (old) => {
        if (!old) return old;
        return old.filter((channel) => channel.id !== data.channelId);
      });

      // Remove single channel cache
      queryClient.removeQueries({ queryKey: ['channels', data.channelId] });

      // Remove messages cache
      queryClient.removeQueries({ queryKey: ['messages', 'infinite', data.channelId] });
    };

    // Handle typing indicator event
    const handleTyping = (data: TypingIndicator) => {
      const conversationKey = data.channelId || data.dmThreadId;
      if (!conversationKey) return;

      if (data.isTyping) {
        addTypingUser(conversationKey, {
          userId: data.userId,
          displayName: data.displayName,
          isBot: data.isBot,
        });

        // Auto-remove after 3 seconds (safety net)
        setTimeout(() => {
          removeTypingUser(conversationKey, data.userId);
        }, 3000);
      } else {
        removeTypingUser(conversationKey, data.userId);
      }
    };

    // Handle reaction event (real-time reaction updates)
    const handleReaction = (data: ReactionEvent) => {
      const conversationKey = data.channelId || data.dmThreadId;
      if (!conversationKey) return;

      const queryKey = ['messages', 'infinite', conversationKey];

      queryClient.setQueryData<InfiniteMessagesData>(queryKey, (old) => {
        if (!old?.pages) return old;

        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            messages: page.messages.map((msg) => {
              if (msg.id !== data.messageId) return msg;

              const reactions = msg.message_reactions || [];

              if (data.action === 'added') {
                const newReaction: MessageReaction = {
                  id: `${data.userId}-${data.emoji}`,
                  message_id: data.messageId,
                  user_id: data.userId,
                  emoji: data.emoji,
                  created_at: new Date().toISOString(),
                };
                return { ...msg, message_reactions: [...reactions, newReaction] };
              } else if (data.action === 'removed') {
                return {
                  ...msg,
                  message_reactions: reactions.filter(
                    (r) => !(r.user_id === data.userId && r.emoji === data.emoji)
                  ),
                };
              }

              return msg;
            }),
          })),
        };
      });
    };

    // Handle presence update event — update last_seen in channel member caches + presence store
    const handlePresenceUpdate = (data: PresenceUpdate) => {
      const lastSeen = data.lastSeen || new Date().toISOString();

      // Update presence store (real-time online/offline tracking)
      const { setUserOnline, setUserOffline } = usePresenceStore.getState();
      if (data.status === 'online') {
        setUserOnline(data.userId);
      } else if (data.status === 'offline') {
        setUserOffline(data.userId);
      }

      // Update all cached channel queries that include members
      type MemberWithLastSeen = { id: string; last_seen?: string };
      type ChannelWithMembers = { members?: MemberWithLastSeen[] };
      queryClient.setQueriesData<ChannelWithMembers | ChannelWithMembers[]>(
        { queryKey: ['channels'] },
        (old) => {
          if (!old) return old;
          // Channel list query (array of channels)
          if (Array.isArray(old)) {
            return old.map((ch) => {
              if (!ch.members || !Array.isArray(ch.members)) return ch;
              const memberIdx = ch.members.findIndex((m) => m.id === data.userId);
              if (memberIdx === -1) return ch;
              const updatedMembers = [...ch.members];
              updatedMembers[memberIdx] = { ...updatedMembers[memberIdx], last_seen: lastSeen };
              return { ...ch, members: updatedMembers };
            });
          }
          // Single channel query (has members array)
          if (old.members && Array.isArray(old.members)) {
            const memberIdx = old.members.findIndex((m) => m.id === data.userId);
            if (memberIdx === -1) return old;
            const updatedMembers = [...old.members];
            updatedMembers[memberIdx] = { ...updatedMembers[memberIdx], last_seen: lastSeen };
            return { ...old, members: updatedMembers };
          }
          return old;
        }
      );
    };

    // Handle initial online users sync from server (+ their custom statuses, feature #43)
    const handleOnlineUsers = (data: { userIds: string[]; statuses?: Record<string, string> }) => {
      const store = usePresenceStore.getState();
      store.setOnlineUsers(data.userIds);
      store.setUserStatuses(data.statuses || {});
    };

    // Handle a colleague's status change (feature #43)
    const handleStatusUpdate = (data: { userId: string; status: string }) => {
      usePresenceStore.getState().setUserStatus(data.userId, data.status as UserStatus);
    };

    // Handle mention event
    const handleMention = async (data: MentionEvent) => {
      console.log('📬 WebSocket: Received mention:new event', data);

      const conversationId = data.channelId || data.dmThreadId || '';
      const conversationType = data.channelId ? 'channel' : 'dm';

      // Add mention to store
      addMention(conversationId, {
        id: data.mentionId,
        message_id: data.message.id,
        mentioned_user_id: data.message.user_id, // Current user was mentioned
        mentioned_by_user_id: data.mentionedBy.id,
        channel_id: data.channelId,
        dm_thread_id: data.dmThreadId,
        is_read: false,
        created_at: new Date().toISOString(),
        message: data.message,
      });

      // Update mention count
      updateMentionCount(conversationId, 1, conversationType);

      // Play mention sound from user settings
      const s = settingsRef.current;
      await soundService.playNotification(
        (s?.mention_sound || 'mention') as SoundType,
        s?.mention_volume ?? 70
      );

      // Show amber toast notification
      const channelName = data.channelId ? `#${data.message.channels?.name || 'channel'}` : 'DM';
      toast.success(`${data.mentionedBy.displayName} mentioned you in ${channelName}`, {
        duration: 6000,
        style: {
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          color: 'white',
          fontWeight: '600',
        },
      });

      // Invalidate mentions queries
      queryClient.invalidateQueries({ queryKey: ['mentions'] });
    };

    // Handle notification event (from user:{userId} room)
    const handleNotification = (data: { notification: { title?: string; message?: string }; timestamp: string }) => {
      console.log('🔔 WebSocket: Received notification:new event', data);

      const notification = data.notification;
      if (notification?.title || notification?.message) {
        toast.info(notification.title || notification.message, {
          duration: 5000,
        });
      }

      // Play system notification sound from user settings
      const s = settingsRef.current;
      soundService.playNotification(
        (s?.system_sound || 'pop') as SoundType,
        s?.system_volume ?? 50
      );

      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    };

    // Handle unread count update (from user:{userId} room)
    const handleUnreadCount = (data: { unreadCount: number; timestamp: string }) => {
      queryClient.setQueryData(['notifications', 'unread-count'], data.unreadCount);
    };

    // Handle other user joining voice channel/room
    const handleVoiceUserJoined = (data: { userId: string; channelId?: string; voiceRoomId?: string }) => {
      // Skip if it's our own join (we already played the sound locally)
      if (currentUserRef.current && data.userId === currentUserRef.current.id) return;
      const s = settingsRef.current;
      soundService.playNotification(
        (s?.voice_join_sound || 'voiceJoin') as SoundType,
        s?.voice_join_volume ?? 50
      );
    };

    // Handle other user leaving voice channel/room
    const handleVoiceUserLeft = (data: { userId: string; channelId?: string; voiceRoomId?: string }) => {
      if (currentUserRef.current && data.userId === currentUserRef.current.id) return;
      const s = settingsRef.current;
      soundService.playNotification(
        (s?.voice_leave_sound || 'voiceLeave') as SoundType,
        s?.voice_leave_volume ?? 50
      );
    };

    // Function to register all event listeners
    const registerAllListeners = () => {
      // Remove all existing app listeners before re-adding to prevent accumulation
      socketClient.removeAllAppListeners();

      socketClient.onNewMessage(handleNewMessage);
      socketClient.onMessageEdited(handleMessageEdited);
      socketClient.onMessageDeleted(handleMessageDeleted);
      socketClient.onMessageUpdated(handleMessageUpdated);
      socketClient.onChannelUpdated(handleChannelUpdated);
      socketClient.onChannelDeleted(handleChannelDeleted);
      socketClient.onTyping(handleTyping);
      socketClient.onReaction(handleReaction);
      socketClient.onPresenceUpdate(handlePresenceUpdate);
      socketClient.getSocket()?.on('presence:online-users', handleOnlineUsers);
      socketClient.onStatusUpdate(handleStatusUpdate);
      socketClient.getSocket()?.on('mention:new', handleMention);
      socketClient.onNotification(handleNotification);
      socketClient.onUnreadCount(handleUnreadCount);
      socketClient.getSocket()?.on('voice:channel-user-joined', handleVoiceUserJoined);
      socketClient.getSocket()?.on('voice:channel-user-left', handleVoiceUserLeft);
      socketClient.getSocket()?.on('voice:room-user-joined', handleVoiceUserJoined);
      socketClient.getSocket()?.on('voice:room-user-left', handleVoiceUserLeft);
    };

    // Callback reference for proper cleanup via offConnect
    const onConnectCallback = () => {
      console.log('🔌 Socket connected, registering listeners now');
      registerAllListeners();
    };

    // Connect to WebSocket when component mounts
    if (!isConnectedRef.current) {
      socketClient.connect();
      socketClient.onConnect(onConnectCallback);
      isConnectedRef.current = true;
    }

    // Register handlers immediately for already-connected socket
    if (socketClient.isConnected()) {
      registerAllListeners();
    }

    // Cleanup on unmount
    return () => {
      socketClient.offConnect(onConnectCallback);
      socketClient.removeAllAppListeners();
      socketClient.disconnect();
      isConnectedRef.current = false;
    };
  }, [queryClient, addTypingUser, removeTypingUser, addMention, updateMentionCount]);

  // Join channel rooms when active channel changes (safety net for channels joined after initial connect)
  // NOTE: Do NOT leave rooms on cleanup — server auto-joins all rooms via RoomsManager.joinUserRooms()
  // Leaving rooms was causing users to only receive events for the active channel
  useEffect(() => {
    if (activeChannelId) {
      socketClient.joinChannel(activeChannelId);
    }
  }, [activeChannelId]);

  // Join DM thread rooms when active DM changes (same pattern — no leave on cleanup)
  useEffect(() => {
    if (activeDMThreadId) {
      socketClient.joinDMThread(activeDMThreadId);
    }
  }, [activeDMThreadId]);
};
